import { AlertCircle, LogOut, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  EMPLOYEE_STATUSES,
  EMPLOYEE_STATUS_LABELS,
  canRecordExit,
  ROLES,
  ROLE_LABELS,
  type EmployeeStatus,
  type Role,
} from '@cfl/assets-shared';

import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { LostArt, TeamArt } from '@/components/illustrations';
import { Typewriter } from '@/components/typewriter';
import { DepartmentPicker } from '@/components/pickers';
import { SortableHead, type SortDirection } from '@/components/sortable-head';
import { PageContainer } from '@/components/shell/page-container';
import { employeeStatusVariant, PILL_CLASS } from '@/lib/badges';
import {
  useDeleteEmployee,
  useDepartments,
  useEmployees,
  type EmployeeSort,
  type EmployeeSummary,
} from '@/lib/employees-api';
import { EmployeeFormDialog } from '@/routes/employee-form';
import { RecordExitDialog } from '@/routes/exits';
import {
  BulkDeleteDialog,
  DeleteRecordDialog,
  SelectionBar,
  useSelection,
} from '@/components/bulk-delete';
import { useBulkDeleteEmployees } from '@/lib/bulk-api';
import { useCan } from '@/lib/session';

const PAGE_SIZE = 25;

export function EmployeesPage() {
  const [params, setParams] = useSearchParams();
  const canManage = useCan('manageEmployees');
  const [adding, setAdding] = React.useState(false);
  const [editing, setEditing] = React.useState<EmployeeSummary | null>(null);
  const canDelete = useCan('deleteRecords');
  const canExit = useCan('runExitClearance');
  const selection = useSelection();
  const bulkDelete = useBulkDeleteEmployees();
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [deletingOne, setDeletingOne] = React.useState<EmployeeSummary | null>(null);
  const [exiting, setExiting] = React.useState<EmployeeSummary | null>(null);
  const deleteEmployee = useDeleteEmployee();

  const search = params.get('search') ?? '';
  const status = (params.get('status') ?? '') as EmployeeStatus | '';
  const role = (params.get('role') ?? '') as Role | '';
  const department = params.get('department') ?? '';
  const holdingAssets = params.get('holdingAssets') === 'true';
  const includeExited = params.get('includeExited') === 'true';
  const page = Number(params.get('page') ?? '1');
  const sort = (params.get('sort') ?? 'name') as EmployeeSort;
  const direction = (params.get('direction') ?? 'asc') as SortDirection;

  const { data: departments } = useDepartments();
  const { data, isPending } = useEmployees({
    search,
    status,
    role,
    department,
    holdingAssets,
    includeExited,
    page,
    pageSize: PAGE_SIZE,
    sort,
    direction,
  });

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '' || value === 'false') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const setSort = (column: EmployeeSort, next: SortDirection) => {
    const params2 = new URLSearchParams(params);
    params2.set('sort', column);
    params2.set('direction', next);
    params2.delete('page');
    setParams(params2, { replace: true });
  };

  const [searchInput, setSearchInput] = React.useState(search);
  React.useEffect(() => setSearchInput(search), [search]);
  React.useEffect(() => {
    if (searchInput === search) return;
    const timer = setTimeout(() => setFilter('search', searchInput), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PageContainer>
      <Card>
        <CardContent className="space-y-4 py-4 sm:py-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              {!searchInput && (
                <span className="pointer-events-none absolute left-8 top-1/2 -translate-y-1/2 truncate text-sm text-muted-foreground">
                  <Typewriter text="Search by name, employee ID or email…" />
                </span>
              )}
              <Input
                className="pl-8"
                aria-label="Search people by name, employee ID or email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <Select
              value={status || 'all'}
              onValueChange={(v) => setFilter('status', v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                {EMPLOYEE_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {EMPLOYEE_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={role || 'all'} onValueChange={(v) => setFilter('role', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Any role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any role</SelectItem>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DepartmentPicker
              departments={departments}
              value={department}
              onChange={(v) => setFilter('department', v)}
              allLabel="Any department"
              className="w-[170px]"
            />

            <Button hint={"Show only people who hold an asset"}
              size="sm"
              variant={holdingAssets ? 'secondary' : 'ghost'}
              onClick={() => setFilter('holdingAssets', holdingAssets ? 'false' : 'true')}
            >
              Holding assets
            </Button>
            <Button hint={"Also show people who have left"}
              size="sm"
              variant={includeExited ? 'secondary' : 'ghost'}
              onClick={() => setFilter('includeExited', includeExited ? 'false' : 'true')}
            >
              {includeExited ? 'Hide exited' : 'Show exited'}
            </Button>

            {canManage && (
              <Button hint={"Add an employee who can sign in with their @innoctive.com Google account"} size="sm" onClick={() => setAdding(true)}>
                <Plus className="mr-1.5 size-4" /> Add employee
              </Button>
            )}
          </div>

          {canDelete && (
            <SelectionBar
              count={selection.ids.length}
              noun="employee"
              onClear={selection.clear}
              onDelete={() => setConfirmingDelete(true)}
            />
          )}

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {canDelete && (
                    <TableHead className="w-[40px]">
                      <Checkbox
                        aria-label="Select every employee on this page"
                        checked={
                          (data?.employees.length ?? 0) > 0 &&
                          data!.employees.every((e) => selection.selected.has(e.id))
                        }
                        onCheckedChange={(on) =>
                          selection.toggleAll(data?.employees.map((e) => e.id) ?? [], on === true)
                        }
                      />
                    </TableHead>
                  )}
                  <SortableHead column="employeeCode" sort={sort} direction={direction} onSort={setSort} className="w-[120px]">
                    Employee ID
                  </SortableHead>
                  <SortableHead column="name" sort={sort} direction={direction} onSort={setSort}>
                    Name
                  </SortableHead>
                  <SortableHead column="department" sort={sort} direction={direction} onSort={setSort} className="hidden md:table-cell">
                    Department
                  </SortableHead>
                  <SortableHead column="role" sort={sort} direction={direction} onSort={setSort} className="hidden lg:table-cell">
                    Role
                  </SortableHead>
                  <SortableHead column="status" sort={sort} direction={direction} onSort={setSort}>
                    Status
                  </SortableHead>
                  <SortableHead column="assetsHeld" sort={sort} direction={direction} onSort={setSort} align="right">
                    Holding
                  </SortableHead>
                  {canManage && <TableHead className={canDelete ? 'w-[210px]' : 'w-[170px]'} />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending ? (
                  <TableRow>
                    <TableCell colSpan={(canManage ? 7 : 6) + (canDelete ? 1 : 0)} className="py-10 text-center text-sm text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : data?.employees.length ? (
                  data.employees.map((employee) => (
                    <TableRow key={employee.id}>
                      {canDelete && (
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${employee.name}`}
                            checked={selection.selected.has(employee.id)}
                            onCheckedChange={() => selection.toggle(employee.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell className="at-num font-medium">
                        <Link to={`/employees/${employee.id}`} className="hover:underline">
                          {employee.employeeCode}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link to={`/employees/${employee.id}`} className="hover:underline">
                          <span className="font-medium">{employee.name}</span>
                        </Link>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {employee.email ?? 'No email'}
                          {/* Surfaced because it is otherwise invisible: an imported
                              record with no email can never sign in to see its own
                              assets, and "why can't I log in" has no other answer. */}
                          {!employee.canSignIn && employee.status !== 'EXITED' && (
                            <span
                              className="flex items-center gap-0.5 text-warning"
                              title="No email on record, so this person cannot sign in"
                            >
                              <AlertCircle className="size-3" /> cannot sign in
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                        {employee.department ?? '—'}
                      </TableCell>
                      <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
                        {ROLE_LABELS[employee.role]}
                      </TableCell>
                      <TableCell>
                        <Badge variant={employeeStatusVariant(employee.status)} className={PILL_CLASS}>
                          {EMPLOYEE_STATUS_LABELS[employee.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="at-num text-right">
                        {employee.assetsHeld > 0 ? employee.assetsHeld : '—'}
                      </TableCell>
                      {canManage && (
                        <TableCell className="whitespace-nowrap text-right">
                          {/* HR's own permission, not manageEmployees: exits and the
                              directory are different jobs, and runExitClearance is what
                              the API guards the exit routes with. Gone once they have
                              left - canRecordExit is the same predicate that now
                              refuses a second exit on the server. */}
                          {canExit && canRecordExit(employee.status) && (
                            <Button hint={"Record that this person is leaving, with what they must hand back"}
                              size="sm"
                              variant="secondary"
                              className="mr-1 h-7 px-2 text-xs"
                              onClick={() => setExiting(employee)}
                            >
                              <LogOut className="mr-1 size-3.5" /> Record exit
                            </Button>
                          )}
                          <Button hint={"Change this employee's details"}
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => setEditing(employee)}
                          >
                            <Pencil className="size-3.5" />
                            <span className="sr-only">Edit {employee.name}</span>
                          </Button>
                          {canDelete && (
                            <Button hint={"Delete this employee — only possible if they have no history"}
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-muted-foreground hover:text-destructive"
                              onClick={() => setDeletingOne(employee)}
                            >
                              <Trash2 className="size-3.5" />
                              <span className="sr-only">Delete {employee.name}</span>
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={(canManage ? 7 : 6) + (canDelete ? 1 : 0)}>
                      {search || status || role || department || holdingAssets ? (
                        <EmptyState art={LostArt} title="Nobody matches those filters" body="Someone who has left only shows with Include exited.">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSearchInput('');
                              setParams(new URLSearchParams(), { replace: true });
                            }}
                          >
                            Clear filters
                          </Button>
                        </EmptyState>
                      ) : (
                        <EmptyState art={TeamArt} title="No employees yet" body="Add people one by one, or import the HR sheet — then issue their hardware.">
                          {canManage && (
                            <Button onClick={() => setAdding(true)}>
                              <Plus className="mr-1.5 size-4" /> Add employee
                            </Button>
                          )}
                        </EmptyState>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm">
              <span className="at-num text-muted-foreground">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setFilter('page', String(page - 1))}>
                  Previous
                </Button>
                <Button size="sm" variant="secondary" disabled={page >= lastPage} onClick={() => setFilter('page', String(page + 1))}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteRecordDialog
        open={deletingOne !== null}
        onOpenChange={(open) => !open && setDeletingOne(null)}
        label={deletingOne ? `${deletingOne.employeeCode} — ${deletingOne.name}` : ''}
        noun="employee"
        alternative="record an exit instead, which closes the account and keeps their history."
        onDelete={() => deleteEmployee.mutateAsync(deletingOne!.id)}
      />

      <BulkDeleteDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        count={selection.ids.length}
        noun="employee"
        onConfirm={async () => {
          const result = await bulkDelete.mutateAsync(selection.ids);
          const kept = new Set(result.skipped.map((sk) => sk.id));
          selection.toggleAll(selection.ids.filter((id) => !kept.has(id)), false);
          return result;
        }}
      />

      {exiting && (
        <RecordExitDialog
          employee={exiting}
          open
          onOpenChange={(open) => !open && setExiting(null)}
        />
      )}

      <EmployeeFormDialog open={adding} onOpenChange={setAdding} />
      <EmployeeFormDialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        employee={editing ?? undefined}
      />
    </PageContainer>
  );
}
